<template>
	<div class="page-container">
		<div class="header-bar">
			<span class="header-back" @click="$router.back()">←</span>
			我的订单
		</div>

		<!-- 订单状态筛选栏 -->
		<div class="status-filter-bar">
			<div v-for="status in statusList" :key="status.value"
				:class="['filter-item', { 'active': currentStatus === status.value }]"
				@click="changeStatusFilter(status.value)">
				{{ status.label }}
			</div>
		</div>

		<!-- 空状态 -->
		<div v-if="!loading && orderList.length === 0" class="empty-state">
			<div class="empty-icon">📦</div>
			<p class="empty-text">暂无订单记录</p>
		</div>

		<div class="card-container" v-else>
			<div v-for="order in orderList" :key="order.id" class="order-card" @click="viewOrderDetail(order)">
				<div class="order-header">
					<div class="order-no">订单号：{{ order.order_no }}</div>
					<span :class="['status-tag', getStatusClass(order.order_status)]">
						{{ getStatusText(order.order_status) }}
					</span>
				</div>

				<div class="order-body">
					<img :src="order.goods_image || defaultImage" :alt="order.goods_name" class="goods-img"
						@error="handleImageError" />
					<div class="goods-info">
						<h3 class="goods-name">{{ order.goods_name }}</h3>
						<p class="goods-meta">
							<span class="goods-no">编码：{{ order.goods_no }}</span>
							<span class="goods-num" v-if="order.num > 1">×{{ order.num }}</span>
						</p>
						<!-- 显示已选择的SKU信息 -->
						<p class="sku-info" v-if="order.sku_id && order.sku_id !== 0 && order.sku_name">
							<span class="sku-tag">SKU: {{ order.sku_name }}</span>
						</p>
						<p class="order-time">{{ formatTime(order.create_time) }}</p>
					</div>
				</div>

				<!-- 配送方式提示（待选择状态时根据商品配置展示） -->
				<div class="delivery-hint" v-if="String(order.order_status) === '-1' && order.delivery_method">
					<span class="delivery-icon">📦</span>
					<span>{{ getDeliveryMethodText(order.delivery_method) }}</span>
				</div>

				<div class="order-actions" v-if="showActionButtons(order)" @click.stop>
					<!-- 选择子商品按钮 -->
					<button v-if="String(order.order_status) === '-1' && needSelectSku(order)"
						class="btn-select-sku action-btn" @click="showSkuDialog(order)">
						选择子商品
					</button>
					<button v-if="String(order.order_status) === '-1' && canExpress(order)"
						class="btn-express action-btn" @click="selectDeliveryMethod(order, 'express')">
						快递到家
					</button>
					<button v-if="String(order.order_status) === '-1' && canPickup(order)" class="btn-pickup action-btn"
						@click="confirmPickup(order)">
						线下领取
					</button>
					<button v-if="order.order_status === 3" class="btn-logistics action-btn"
						@click="viewLogistics(order)">
						查看物流
					</button>
					<button v-if="order.order_status === 1" class="btn-edit-address action-btn"
						@click="editOrderAddress(order)">
						修改地址
					</button>
				</div>

				<!-- 收货地址信息（待发货/已出库/已发货状态显示） -->
				<div class="address-section" v-if="order.address_info && [1, 2, 3].includes(order.order_status)">
					<div class="address-icon">📍</div>
					<div class="address-detail">
						<p class="address-name-phone">
							<span class="consignee-name">{{ order.address_info.consignee_name }}</span>
							<span class="consignee-phone">{{ order.address_info.consignee_phone }}</span>
						</p>
						<p class="address-full">
							{{ order.address_info.full_address || (order.address_info.province + order.address_info.city + order.address_info.district + order.address_info.detail_address) }}
						</p>
					</div>
				</div>

				<!-- 物流信息提示 -->
				<div class="logistics-hint" v-if="order.logistics_no && order.order_status >= 2">
					<span class="logistics-icon">🚚</span>
					<span>{{ order.logistics_company_name || '快递运输中' }}：{{ order.logistics_no }}</span>
				</div>

				<!-- 待线下领取区域 (order_status=5) -->
				<div class="verify-code-section" v-if="order.order_status === 5">
					<div class="verify-code-row">
						<span class="label">📱 核销码：</span>
						<span class="code-masked">{{ maskVerifyCode(order.verify_code) }}</span>
					</div>
					<button class="btn-qr-code action-btn" @click.stop="showQRCodeDialog(order)">
						📷 查看二维码
					</button>
					<p class="expire-hint">⏰ 建议尽快到店核销</p>
				</div>

				<!-- 快递服务码区域 (delivery_type=1 且 order_status=1 待发货状态) -->
				<div class="service-code-section"
					v-if="order.delivery_type === 1 && order.order_status === 1 && order.service_code" @click.stop>

					<div class="service-code-display">
						<span class="service-code-value">{{ order.service_code }}</span>
					</div>
				</div>

				<!-- 已线下核销区域 (order_status=6) -->
				<div class="verified-info" v-if="order.order_status === 6">
					<p>🏪 核销门店：{{ order.store_name }}</p>
					<p>👤 核销人员：{{ order.staff_name }} {{ maskPhone(order.staff_phone) }}</p>
					<p>🕐 核销时间：{{ formatTime(order.verify_time) }}</p>
				</div>
			</div>
		</div>

		<!-- 加载更多 -->
		<div class="load-more" v-if="orderList.length > 0">
			<button v-if="orderList.length < total" @click="handleLoadMore" :disabled="loading" class="btn-load-more">
				{{ loading ? '加载中...' : '加载更多' }}
			</button>
			<p v-else class="no-more">— 已经到底了 —</p>
		</div>

		<SelectAddressDialog :visible.sync="addressDialogVisible" :selected-address-id.sync="selectedAddressId"
			@confirm="handleAddressConfirm" />

		<ConfirmPickupDialog :visible.sync="pickupDialogVisible" :order-info="currentOrder"
			@confirm="handlePickupConfirm" />

		<!-- 二维码查看弹窗 -->
		<el-dialog :visible.sync="showQRCodeDialogVisible" title="核销二维码" width="85%" :show-close="true"
			:close-on-click-modal="true" custom-class="qr-code-dialog">
			<div class="qr-dialog-content">
				<canvas ref="largeQrCanvas" class="large-qr-canvas"></canvas>

				<div class="qr-code-info">
					<p class="full-code">
						核销码：<strong>{{ currentQRCodeOrder.verify_code }}</strong>
					</p>
					<p class="security-warning">
						⚠️ 此码仅限本人使用，请勿泄露给他人
					</p>
				</div>

				<button type="button" class="btn-close-qr" @click="showQRCodeDialogVisible = false">
					关闭
				</button>
			</div>
		</el-dialog>

		<!-- 服务码弹窗（快递订单） -->
		<el-dialog :visible.sync="showServiceCodeDialogVisible" title="快递服务码" width="85%" :show-close="true"
			:close-on-click-modal="true" custom-class="service-code-dialog">
			<div class="service-dialog-content">
				<div class="service-icon">📦</div>
				<p class="service-title">已成功选择快递到家</p>

				<div class="service-code-box">
					<p class="service-code">{{ currentServiceCodeOrder.service_code }}</p>
				</div>

				<div class="service-info">
					<p><strong>订单号：</strong>{{ currentServiceCodeOrder.order_no }}</p>
				</div>

				<button type="button" class="btn-close-service" @click="showServiceCodeDialogVisible = false">
					我知道了
				</button>
			</div>
		</el-dialog>

		<!-- SKU选择弹窗 -->
		<el-dialog :visible.sync="skuDialogVisible" title="选择子商品" width="90%" :show-close="true"
			:close-on-click-modal="true" custom-class="sku-dialog" @close="handleSkuDialogClose">
			<div class="sku-dialog-content">
				<div v-if="skuLoading" class="sku-loading">
					加载中...
				</div>
				<div v-else-if="skuList.length === 0" class="sku-empty">
					暂无可用子商品
				</div>
				<div v-else class="sku-list">
					<div v-for="sku in skuList" :key="sku.id"
						:class="['sku-item', { 'selected': selectedSkuId === sku.id }]"
						@click="selectSkuItem(sku)">
						<img :src="getImageUrl(sku.sku_image) || defaultImage" :alt="sku.sku_name" class="sku-img"
							@error="handleImageError" />
						<div class="sku-detail">
							<h4 class="sku-name">{{ sku.sku_name }}</h4>
							<p class="sku-no">编码：{{ sku.sku_no }}</p>
						</div>
						<div class="sku-check" v-if="selectedSkuId === sku.id">✓</div>
					</div>
				</div>

				<div class="sku-dialog-footer" v-if="skuList.length > 0">
					<button type="button" class="btn-cancel-sku" @click="skuDialogVisible = false">
						取消
					</button>
					<button type="button" class="btn-confirm-sku" @click="handleConfirmSku"
						:disabled="!selectedSkuId">
						确认选择
					</button>
				</div>
			</div>
		</el-dialog>
	</div>
</template>

<script>
	import SelectAddressDialog from '../components/SelectAddressDialog.vue'
	import ConfirmPickupDialog from '../components/ConfirmPickupDialog.vue'
	import {
		getOrderList,
		selectExpress,
		updateOrderAddress,
		getSkuList,
		selectSku
	} from '../api/order'
	import { getImageUrl } from '../utils/image'
	import QRCode from 'qrcode'

	export default {
		name: 'OrderList',
		components: {
			SelectAddressDialog,
			ConfirmPickupDialog
		},
		data() {
			return {
				loading: false,
				addressDialogVisible: false,
				isEditingAddress: false, // true=修改地址模式, false=首次选择地址模式
				pickupDialogVisible: false,
				currentOrder: null,
				selectedAddressId: null,
				page: 1,
				pageSize: 10,
				total: 0,
				orderList: [],
				currentStatus: '', // 当前选中的筛选状态（空字符串表示全部）
				defaultImage: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHZpZXdCb3g9IjAgMCA4MCA4MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iODAiIGhlaWdodD0iODAiIGZpbGw9IiNmNWY3ZmEiLz48cGF0aCBkPSJNMzAgNDBoMjB2MjBIMzB6IiBmaWxsPSIjZGNkZmU2Ii8+PHRleHQgeD0iNDAiIHk9IjU1IiBmb250LXNpemU9IjEyIiBmaWxsPSIjYzBjNGNjIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj7nu4nkuoY8L3RleHQ+PC9zdmc+',
				statusList: [{
						value: '',
						label: '全部'
					},
					{
						value: '-1',
						label: '待选择'
					},
					{
						value: '1',
						label: '待发货'
					},
					{
						value: '2',
						label: '已出库'
					},
					{
						value: '3',
						label: '已发货'
					},
					{
						value: '5',
						label: '待线下领取'
					},
					{
						value: '6',
						label: '已线下核销'
					}
				],
				showQRCodeDialogVisible: false,
				currentQRCodeOrder: {},
				showServiceCodeDialogVisible: false,
				currentServiceCodeOrder: {},
				skuDialogVisible: false,
				skuLoading: false,
				skuList: [],
				selectedSkuId: null,
				currentSkuOrder: null
			}
		},
		created() {
			this.loadOrderList()
		},
		methods: {
			async loadOrderList(isLoadMore = false) {
				if (this.loading) return

				if (!isLoadMore) {
					this.page = 1
					this.loading = true
				}

				const loadingInstance = this.$loading({
					lock: !isLoadMore,
					text: isLoadMore ? '' : '加载中...',
					spinner: 'el-icon-loading',
					background: 'rgba(255, 255, 255, 0.9)'
				})

				try {
					// 构建请求参数
					const params = {
						page: this.page,
						page_size: this.pageSize
					}

					// 如果选择了特定状态（非全部），则添加筛选条件
					if (this.currentStatus !== '' && this.currentStatus !== null && this.currentStatus !== undefined) {
						params.order_status = Number(this.currentStatus) // 将字符串转为数字传给后端
					}

					const res = await getOrderList(params)

					const list = res.data && res.data.list ? res.data.list : []
					const total = res.data && res.data.total ? res.data.total : 0

					if (isLoadMore) {
						this.orderList = [...this.orderList, ...list]
					} else {
						this.orderList = list
					}

					this.total = total
				} catch (error) {
					console.error('加载订单列表失败:', error)
					this.$message.error(error.msg || error.message || '加载失败')
				} finally {
					this.loading = false
					if (loadingInstance) {
						loadingInstance.close()
					}
				}
			},

			/**
			 * 切换订单状态筛选
			 * @param {number|string} status 状态值（空字符串表示全部）
			 */
			changeStatusFilter(status) {
				if (this.currentStatus === status) return // 避免重复点击

				this.currentStatus = status
				this.loadOrderList() // 重新加载列表
			},

			getStatusText(status) {
				const statusMap = {
					'-1': '待选择',
					'1': '待发货',
					'2': '已出库',
					'3': '已发货',
					'4': '已线下领取',
					'5': '待线下领取',
					'6': '已线下核销'
				}
				return statusMap[String(status)] || '未知状态'
			},

			/**
			 * 获取状态对应的CSS类名
			 * @param {number|string} status 订单状态值
			 * @return {string} CSS类名
			 */
			getStatusClass(status) {
				const statusStr = String(status)
				const classMap = {
					'-1': 'status-neg1',
					'1': 'status-1',
					'2': 'status-2',
					'3': 'status-3',
					'4': 'status-4',
					'5': 'status-5',
					'6': 'status-6'
				}
				return classMap[statusStr] || ''
			},

			formatTime(timeStr) {
				if (!timeStr) return ''
				const date = new Date(timeStr.replace(/-/g, '/'))
				const year = date.getFullYear()
				const month = String(date.getMonth() + 1).padStart(2, '0')
				const day = String(date.getDate()).padStart(2, '0')
				const hours = String(date.getHours()).padStart(2, '0')
				const minutes = String(date.getMinutes()).padStart(2, '0')
				return `${year}-${month}-${day} ${hours}:${minutes}`
			},

			handleImageError(e) {
				e.target.src = this.defaultImage
			},

			showActionButtons(order) {
				const status = String(order.order_status)
				return status === '-1' ||
					status === '1' ||
					status === '3'
			},

			/**
			 * 判断订单是否可以选择快递到家
			 * 配送方式为1(仅线上) 或 3(支持线上线下) 时允许
			 * 但如果商品有子商品且未选择，则不允许
			 */
			canExpress(order) {
				// 先检查是否需要选择SKU
				if (this.needSelectSku(order)) {
					return false
				}
				const method = parseInt(order.delivery_method)
				return method === 1 || method === 3
			},

			/**
			 * 判断订单是否可以选择线下领取
			 * 配送方式为2(仅线下) 或 3(支持线上线下) 时允许
			 * 但如果商品有子商品且未选择，则不允许
			 */
			canPickup(order) {
				// 先检查是否需要选择SKU
				if (this.needSelectSku(order)) {
					return false
				}
				const method = parseInt(order.delivery_method)
				return method === 2 || method === 3
			},

			/**
			 * 获取配送方式文案（用于待选择状态下的提示）
			 */
			getDeliveryMethodText(method) {
				const map = {
					1: '该商品仅支持快递配送',
					2: '该商品仅支持到店领取',
					3: '请选择配送方式'
				}
				return map[String(method)] || ''
			},
			viewOrderDetail(order) {
				console.log('查看订单详情:', order)
			},
			editOrderAddress(order) {
				this.currentOrder = order
				this.selectedAddressId = order.address_id || null
				this.isEditingAddress = true
				this.addressDialogVisible = true
			},
			selectDeliveryMethod(order, method) {
				this.currentOrder = order
				if (method === 'express') {
					this.isEditingAddress = false
					this.selectedAddressId = null
					this.addressDialogVisible = true
				}
			},
			confirmPickup(order) {
				this.currentOrder = order
				this.pickupDialogVisible = true
			},
			async handleAddressConfirm(address) {
				if (!address) {
					this.$message.warning('请选择收货地址')
					return
				}

				const loading = this.$loading({
					lock: true,
					text: '提交中...',
					spinner: 'el-icon-loading',
					background: 'rgba(255, 255, 255, 0.9)'
				})

				try {
					let res
					if (this.isEditingAddress) {
						// 修改地址模式：调用 updateOrderAddress
						res = await updateOrderAddress({
							order_id: this.currentOrder.id,
							address_id: address.id
						})
					} else {
						// 首次选择快递模式：调用 selectExpress
						res = await selectExpress({
							order_id: this.currentOrder.id,
							address_id: address.id
						})
					}

					loading.close()
					this.addressDialogVisible = false

					// 判断是否返回了服务码（仅首次选择快递时返回）
					if (res.data && res.data.service_code) {
						this.showServiceCodeDialog(res.data)
					} else {
						this.$message.success(res.msg || '操作成功')
					}

					this.currentOrder = null
					this.selectedAddressId = null
					this.isEditingAddress = false

					this.loadOrderList()
				} catch (error) {
					loading.close()
					console.error('操作失败:', error)
					this.$message.error(error.msg || error.message || '操作失败')
				}
			},
			async handlePickupConfirm() {
				this.currentOrder = null
				this.loadOrderList()
			},
			viewLogistics(order) {
				this.$router.push(`/logistics/${order.id}`)
			},
			handleLoadMore() {
				if (this.loading || this.orderList.length >= this.total) return
				this.page++
				this.loadOrderList(true)
			},
			maskVerifyCode(code) {
				if (!code || code.length !== 6) return code || '-'
				return code.substring(0, 2) + '****' + code.substring(4)
			},
			maskPhone(phone) {
				if (!phone || phone.length !== 11) return phone || '-'
				return phone.substring(0, 3) + '****' + phone.substring(7)
			},
			showQRCodeDialog(order) {
				this.currentQRCodeOrder = order
				this.showQRCodeDialogVisible = true
				this.$nextTick(() => {
					this.generateLargeQRCode(order.verify_code)
				})
			},
			async generateLargeQRCode(code) {
				try {
					const canvas = this.$refs.largeQrCanvas
					await QRCode.toCanvas(canvas, code, {
						width: 256,
						height: 256,
						margin: 2
					})
				} catch (err) {
					console.error('生成二维码失败:', err)
				}
			},
			showServiceCodeDialog(data) {
				this.currentServiceCodeOrder = {
					service_code: data.service_code,
					order_no: data.order_no,
					order_id: data.order_id
				}
				this.showServiceCodeDialogVisible = true
			},
			copyServiceCode(code) {
				// 创建临时输入框用于复制
				const input = document.createElement('input')
				input.value = code
				document.body.appendChild(input)
				input.select()
				input.setSelectionRange(0, 99999) // 移动端兼容

				try {
					document.execCommand('copy')
					this.$message.success('服务码已复制：' + code)
				} catch (err) {
					console.error('复制失败:', err)
					this.$message.error('复制失败，请手动复制')
				}

				document.body.removeChild(input)
			},
			/**
			 * 判断订单是否需要选择SKU
			 * 商品有子商品（has_sku为true）且未选择（sku_id为0或不存在）
			 */
			needSelectSku(order) {
				// 如果有has_sku字段，直接使用
				if (order.has_sku !== undefined) {
					return order.has_sku && (!order.sku_id || order.sku_id === 0)
				}
				// 否则根据sku_id判断（兼容旧数据）
				return !order.sku_id || order.sku_id === 0
			},
			/**
			 * 显示SKU选择弹窗
			 */
			async showSkuDialog(order) {
				this.currentSkuOrder = order
				this.skuDialogVisible = true
				this.selectedSkuId = null
				await this.loadSkuList()
			},
			/**
			 * 加载SKU列表
			 */
			async loadSkuList() {
				if (!this.currentSkuOrder) return

				this.skuLoading = true
				this.skuList = []

				try {
					const res = await getSkuList(this.currentSkuOrder.id)
					this.skuList = res.data && res.data.list ? res.data.list : []
				} catch (error) {
					console.error('加载SKU列表失败:', error)
					this.$message.error(error.msg || error.message || '加载SKU列表失败')
					this.skuDialogVisible = false
				} finally {
					this.skuLoading = false
				}
			},
			/**
			 * 选择SKU项
			 */
			selectSkuItem(sku) {
				this.selectedSkuId = sku.id
			},
			/**
			 * 确认选择SKU
			 */
			async handleConfirmSku() {
				if (!this.selectedSkuId) {
					this.$message.warning('请选择一个子商品')
					return
				}

				const loading = this.$loading({
					lock: true,
					text: '提交中...',
					spinner: 'el-icon-loading',
					background: 'rgba(255, 255, 255, 0.9)'
				})

				try {
					await selectSku({
						order_id: this.currentSkuOrder.id,
						sku_id: this.selectedSkuId
					})

					loading.close()
					this.skuDialogVisible = false
					this.$message.success('选择成功')

					// 重新加载订单列表
					this.loadOrderList()
				} catch (error) {
					loading.close()
					console.error('选择SKU失败:', error)
					this.$message.error(error.msg || error.message || '选择失败')
				}
			},
			/**
			 * 处理图片URL（使用统一的工具函数）
			 */
			getImageUrl,
			/**
			 * SKU弹窗关闭时的处理
			 */
			handleSkuDialogClose() {
				this.currentSkuOrder = null
				this.skuList = []
				this.selectedSkuId = null
			}
		}
	}
</script>

<style scoped>
	.page-container {
		min-height: 100vh;
		background: #f5f7fa;
	}

	/* 状态筛选栏 */
	.status-filter-bar {
		display: flex;
		align-items: center;
		background: #fff;
		padding: 12px 16px;
		margin-bottom: 10px;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
		position: sticky;
		top: 0;
		z-index: 10;
		overflow-x: auto;
		-webkit-overflow-scrolling: touch;
	}

	.status-filter-bar::-webkit-scrollbar {
		display: none;
	}

	.filter-item {
		flex-shrink: 0;
		text-align: center;
		padding: 8px 14px;
		font-size: 13px;
		color: #606266;
		font-weight: 500;
		position: relative;
		cursor: pointer;
		transition: all 0.3s ease;
		border-radius: 6px;
		white-space: nowrap;
	}

	.filter-item:active {
		transform: scale(0.95);
	}

	.filter-item.active {
		color: #409eff;
		font-weight: 600;
		background: #ecf5ff;
	}

	.filter-item.active::after {
		content: '';
		position: absolute;
		bottom: 2px;
		left: 50%;
		transform: translateX(-50%);
		width: 20px;
		height: 3px;
		background: linear-gradient(90deg, #409eff 0%, #66b1ff 100%);
		border-radius: 2px;
	}

	/* 空状态 */
	.empty-state {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 100px 20px;
	}

	.empty-icon {
		font-size: 64px;
		margin-bottom: 16px;
	}

	.empty-text {
		font-size: 15px;
		color: #909399;
	}

	.order-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 14px;
		padding-bottom: 12px;
		border-bottom: 1px solid #f0f0f0;
	}

	.order-no {
		font-size: 13px;
		color: #909399;
		flex: 1;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		margin-right: 10px;
	}

	/* 状态标签 */
	.status-tag {
		display: inline-block;
		padding: 3px 10px;
		border-radius: 12px;
		font-size: 12px;
		font-weight: 500;
		white-space: nowrap;
	}

	.status-neg1 {
		background: #fef0e6;
		color: #e6a23c;
	}

	.status-1 {
		background: #ecf5ff;
		color: #409eff;
	}

	.status-2 {
		background: #f4f4f5;
		color: #909399;
	}

	.status-3 {
		background: #f0f9eb;
		color: #67c23a;
	}

	.order-body {
		display: flex;
		gap: 14px;
		margin-bottom: 16px;
	}

	/* 配送方式提示 */
	.delivery-hint {
		margin-bottom: 12px;
		padding: 8px 12px;
		background: #f0f9ff;
		border-radius: 8px;
		font-size: 13px;
		color: #409eff;
		display: flex;
		align-items: center;
		gap: 6px;
	}

	.delivery-icon {
		flex-shrink: 0;
	}

	.goods-img {
		width: 80px;
		height: 80px;
		border-radius: 10px;
		object-fit: cover;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
		flex-shrink: 0;
		background: #f5f7fa;
	}

	.goods-info {
		flex: 1;
		display: flex;
		flex-direction: column;
		justify-content: space-between;
	}

	.goods-name {
		font-size: 15px;
		font-weight: 600;
		color: #303133;
		line-height: 1.4;
		margin-bottom: 6px;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}

	.goods-meta {
		font-size: 12px;
		color: #909399;
		margin-bottom: 4px;
		display: flex;
		align-items: center;
		gap: 8px;
	}

	.goods-num {
		color: #f56c6c;
		font-weight: 500;
	}

	.order-time {
		font-size: 11px;
		color: #c0c4cc;
	}

	.order-actions {
		display: flex;
		gap: 10px;
		padding-top: 14px;
		border-top: 1px solid #f0f0f0;
		justify-content: flex-end;
		flex-wrap: wrap;
	}

	.action-btn {
		min-width: 96px;
		height: 34px;
		font-size: 13px;
		padding: 0 16px;
		border-radius: 17px;
		border: none;
		cursor: pointer;
		transition: all 0.3s;
		font-weight: 500;
	}

	.btn-express {
		background: linear-gradient(135deg, #409eff 0%, #66b1ff 100%);
		color: #fff;
	}

	.btn-express:active {
		transform: scale(0.95);
	}

	.btn-pickup {
		background: linear-gradient(135deg, #e6a23c 0%, #ebb563 100%);
		color: #fff;
	}

	.btn-pickup:active {
		transform: scale(0.95);
	}

	.btn-logistics {
		background: linear-gradient(135deg, #67c23a 0%, #85ce61 100%);
		color: #fff;
	}

	.btn-logistics:active {
		transform: scale(0.95);
	}

	.btn-edit-address {
		background: linear-gradient(135deg, #909399 0%, #a6a9ad 100%);
		color: #fff;
	}

	.btn-edit-address:active {
		transform: scale(0.95);
	}

	/* 收货地址信息 */
	.address-section {
		margin-top: 12px;
		padding: 10px 12px;
		background: #fdf6ec;
		border-radius: 8px;
		display: flex;
		align-items: flex-start;
		gap: 8px;
	}

	.address-icon {
		font-size: 16px;
		flex-shrink: 0;
		margin-top: 2px;
	}

	.address-detail {
		flex: 1;
		min-width: 0;
	}

	.address-name-phone {
		font-size: 14px;
		color: #303133;
		font-weight: 500;
		margin: 0 0 4px;
	}

	.consignee-name {
		margin-right: 10px;
	}

	.consignee-phone {
		color: #909399;
		font-weight: 400;
	}

	.address-full {
		font-size: 13px;
		color: #606266;
		margin: 0;
		line-height: 1.4;
		word-break: break-all;
	}

	/* 物流信息提示 */
	.logistics-hint {
		margin-top: 12px;
		padding: 10px 12px;
		background: #f0f9eb;
		border-radius: 8px;
		font-size: 12px;
		color: #67c23a;
		display: flex;
		align-items: center;
		gap: 6px;
	}

	.logistics-icon {
		font-size: 16px;
	}

	/* 加载更多 */
	.load-more {
		padding: 20px;
		text-align: center;
	}

	.btn-load-more {
		width: 100%;
		max-width: 300px;
		height: 40px;
		background: #fff;
		border: 1px solid #dcdfe6;
		border-radius: 20px;
		color: #606266;
		font-size: 14px;
		cursor: pointer;
		transition: all 0.3s;
	}

	.btn-load-more:active {
		background: #f5f7fa;
		border-color: #c0c4cc;
	}

	.btn-load-more:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}

	.no-more {
		font-size: 13px;
		color: #c0c4cc;
		margin: 0;
	}

	/* 核销相关样式 */
	.verify-code-section {
		margin-top: 12px;
		padding-top: 12px;
		border-top: 1px dashed #eee;
	}

	.verify-code-row {
		display: flex;
		align-items: center;
		margin-bottom: 8px;
		font-size: 13px;
	}

	.verify-code-row .label {
		color: #606266;
	}

	.code-masked {
		font-family: 'Courier New', monospace;
		font-weight: bold;
		color: #07c160;
		letter-spacing: 2px;
		margin-left: 5px;
	}

	.btn-qr-code {
		min-width: 120px;
		height: 34px;
		font-size: 13px;
		padding: 0 16px;
		border-radius: 17px;
		border: none;
		cursor: pointer;
		transition: all 0.3s;
		font-weight: 500;
		background: linear-gradient(135deg, #e6a23c 0%, #ebb563 100%);
		color: #fff;
	}

	.btn-qr-code:active {
		transform: scale(0.95);
	}

	.expire-hint {
		font-size: 12px;
		color: #ff976a;
		margin: 8px 0 0 0;
	}

	.verified-info {
		margin-top: 12px;
		padding-top: 12px;
		border-top: 1px dashed #eee;
		font-size: 13px;
		color: #666;
		line-height: 1.8;
	}

	.verified-info p {
		margin: 4px 0;
	}

	/* 状态标签扩展 */
	.status-4 {
		background: #fef0f0;
		color: #f56c6c;
	}

	.status-5 {
		background: #fdf6ec;
		color: #e6a23c;
	}

	.status-6 {
		background: #f0f9eb;
		color: #67c23a;
	}

	/* 二维码弹窗样式 */
	.qr-dialog-content {
		text-align: center;
		padding: 20px 0;
	}

	.large-qr-canvas {
		display: block;
		margin: 0 auto 15px;
	}

	.qr-code-info {
		margin: 15px 0;
	}

	.full-code {
		font-family: 'Courier New', monospace;
		font-size: 18px;
		color: #07c160;
		margin: 10px 0;
	}

	.security-warning {
		color: #ff976a;
		font-size: 13px;
		margin: 10px 0;
	}

	.btn-close-qr {
		width: 100%;
		max-width: 200px;
		height: 40px;
		background: linear-gradient(135deg, #409eff 0%, #66b1ff 100%);
		color: #fff;
		border: none;
		border-radius: 20px;
		font-size: 14px;
		cursor: pointer;
		transition: all 0.3s;
		margin-top: 10px;
	}

	.btn-close-qr:active {
		transform: scale(0.95);
	}

	/* 服务码弹窗样式 */
	.service-dialog-content {
		text-align: center;
		padding: 20px 0;
	}

	.service-icon {
		font-size: 48px;
		margin-bottom: 10px;
	}

	.service-title {
		font-size: 16px;
		color: #67c23a;
		margin: 10px 0 20px;
		font-weight: 600;
	}

	.service-code-box {
		background: linear-gradient(135deg, #f0f9eb 0%, #e8f5e9 100%);
		border-radius: 12px;
		padding: 20px 15px;
		margin: 15px 0;
	}

	.service-label {
		font-size: 13px;
		color: #67c23a;
		margin: 0 0 10px 0;
	}

	.service-code {
		font-family: 'Courier New', monospace;
		font-size: 32px;
		color: #409eff;
		font-weight: bold;
		letter-spacing: 6px;
		margin: 0;
	}

	.service-info {
		margin: 15px 0;
		font-size: 14px;
		color: #606266;
		line-height: 1.8;
	}

	.service-hint {
		font-size: 12px;
		color: #909399;
		margin-top: 8px;
	}

	.btn-close-service {
		width: 100%;
		max-width: 200px;
		height: 40px;
		background: linear-gradient(135deg, #67c23a 0%, #85ce61 100%);
		color: #fff;
		border: none;
		border-radius: 20px;
		font-size: 14px;
		cursor: pointer;
		transition: all 0.3s;
		margin-top: 10px;
	}

	.btn-close-service:active {
		transform: scale(0.95);
	}

	/* 快递服务码区域样式（订单列表中显示） */
	.service-code-section {
		background: linear-gradient(135deg, #f0f9eb 0%, #e8f5e9 100%);
		border-radius: 12px;
		padding: 12px 15px;
		margin-top: 12px;
		border: 1px solid #c2e7b0;
	}

	.service-code-header {
		display: flex;
		align-items: center;
		gap: 6px;
		margin-bottom: 8px;
	}

	.service-icon {
		font-size: 18px;
	}

	.service-title {
		font-size: 14px;
		color: #67c23a;
		font-weight: 600;
	}

	.service-code-display {
		display: flex;
		flex-direction: column;
		gap: 4px;
		margin-bottom: 10px;
	}

	.service-label {
		font-size: 12px;
		color: #67c23a;
	}

	.service-code-value {
		font-family: 'Courier New', monospace;
		font-size: 24px;
		color: #409eff;
		font-weight: bold;
		letter-spacing: 4px;
		background: rgba(255, 255, 255, 0.8);
		padding: 8px 12px;
		border-radius: 8px;
		display: inline-block;
	}

	.btn-copy-service-code {
		width: auto;
		height: 32px;
		background: linear-gradient(135deg, #409eff 0%, #66b1ff 100%);
		color: #fff;
		border: none;
		border-radius: 16px;
		font-size: 13px;
		padding: 0 16px;
		cursor: pointer;
		transition: all 0.3s;
		margin: 8px auto 0;
		display: block;
	}

	.btn-copy-service-code:active {
		transform: scale(0.95);
	}

	.service-hint {
		font-size: 12px;
		color: #909399;
		margin-top: 6px;
		text-align: center;
	}

	/* SKU信息显示样式 */
	.sku-info {
		margin: 4px 0;
	}

	.sku-tag {
		display: inline-block;
		padding: 2px 8px;
		background: #ecf5ff;
		border-radius: 4px;
		font-size: 12px;
		color: #409eff;
	}

	/* 选择子商品按钮样式 */
	.btn-select-sku {
		background: linear-gradient(135deg, #f56c6c 0%, #f78989 100%);
		color: #fff;
	}

	.btn-select-sku:active {
		transform: scale(0.95);
	}

	/* SKU选择弹窗样式 */
	.sku-dialog-content {
		padding: 10px 0;
	}

	.sku-loading,
	.sku-empty {
		text-align: center;
		padding: 40px 20px;
		color: #909399;
		font-size: 14px;
	}

	.sku-list {
		max-height: 400px;
		overflow-y: auto;
	}

	.sku-item {
		display: flex;
		align-items: center;
		gap: 12px;
		padding: 12px;
		margin-bottom: 10px;
		background: #f9fafc;
		border-radius: 10px;
		cursor: pointer;
		transition: all 0.3s;
		border: 2px solid transparent;
	}

	.sku-item:active {
		transform: scale(0.98);
	}

	.sku-item.selected {
		background: #ecf5ff;
		border-color: #409eff;
	}

	.sku-img {
		width: 60px;
		height: 60px;
		border-radius: 8px;
		object-fit: cover;
		flex-shrink: 0;
		background: #fff;
	}

	.sku-detail {
		flex: 1;
		min-width: 0;
	}

	.sku-name {
		font-size: 14px;
		font-weight: 600;
		color: #303133;
		margin: 0 0 6px 0;
		line-height: 1.4;
	}

	.sku-no {
		font-size: 12px;
		color: #909399;
		margin: 0;
	}

	.sku-check {
		width: 24px;
		height: 24px;
		background: #409eff;
		color: #fff;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 14px;
		font-weight: bold;
		flex-shrink: 0;
	}

	.sku-dialog-footer {
		display: flex;
		gap: 10px;
		margin-top: 20px;
		padding-top: 15px;
		border-top: 1px solid #eee;
	}

	.btn-cancel-sku,
	.btn-confirm-sku {
		flex: 1;
		height: 40px;
		border-radius: 20px;
		border: none;
		font-size: 14px;
		cursor: pointer;
		transition: all 0.3s;
	}

	.btn-cancel-sku {
		background: #f5f7fa;
		color: #606266;
	}

	.btn-cancel-sku:active {
		transform: scale(0.95);
	}

	.btn-confirm-sku {
		background: linear-gradient(135deg, #409eff 0%, #66b1ff 100%);
		color: #fff;
	}

	.btn-confirm-sku:active {
		transform: scale(0.95);
	}

	.btn-confirm-sku:disabled {
		background: #c0c4cc;
		cursor: not-allowed;
	}
</style>